import {
  Key as KeyIcon,
  LayoutGrid,
  LogOut,
  Plus,
  ShieldCheck,
} from "lucide-react";
import * as React from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { cn } from "@/shared/lib/cn";
import { useAuthStore } from "@/shared/lib/auth-store";

interface Item {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  cta?: boolean;
  adminOnly?: boolean;
}

const ITEMS: Item[] = [
  { to: "/", label: "Проекты", icon: LayoutGrid },
  // /projects/new появится в этапе 7. Сейчас роута нет — fallback кинет на «/».
  { to: "/projects/new", label: "Новый проект", icon: Plus, cta: true },
  { to: "/settings/keys", label: "API ключи", icon: KeyIcon },
  { to: "/admin", label: "Админка", icon: ShieldCheck, adminOnly: true },
];

export function Sidebar(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  return (
    <aside className="flex w-[248px] shrink-0 flex-col gap-8 border-r border-white/10 bg-white/[0.02] px-4 py-6">
      <Logo />

      <nav className="flex flex-col gap-1">
        {ITEMS.filter((it) => !it.adminOnly || user?.is_admin).map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                  isActive
                    ? "border border-purple/45 bg-gradient-to-r from-purple/[0.18] to-cyan/[0.10] text-white shadow-glow-purple"
                    : it.cta
                      ? "border border-dashed border-purple/35 bg-gradient-to-r from-purple/[0.10] to-cyan/[0.06] text-foreground/90"
                      : "border border-transparent text-foreground/80 hover:bg-white/[0.04]",
                )
              }
            >
              <Icon className={cn("h-[18px] w-[18px] shrink-0")} />
              <span className="flex-1 truncate">{it.label}</span>
              {it.adminOnly && (
                <span className="rounded bg-amber/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber">
                  ADMIN
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="mt-auto flex flex-col gap-2 px-1">
        {user && (
          <div className="flex items-center gap-3 rounded-md px-2 py-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-purple to-pink text-xs font-bold text-white">
              {user.email[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-medium text-white">{user.email}</div>
              {user.is_admin && (
                <div className="text-[10px] text-amber">админ</div>
              )}
            </div>
            <button
              type="button"
              aria-label="Выйти"
              className="rounded p-1.5 text-muted-foreground hover:bg-white/[0.05] hover:text-white"
              onClick={() => {
                logout();
                navigate("/login", { replace: true });
              }}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

function Logo(): JSX.Element {
  return (
    <div className="flex items-center gap-3 px-1">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-primary text-sm font-bold text-white shadow-glow-purple">
        SC
      </div>
      <div className="leading-tight">
        <div className="text-base font-bold text-white">
          Semantic<span className="text-purple">.</span>Core
        </div>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
          конвейер семантики
        </div>
      </div>
    </div>
  );
}
